import {messages} from '@smartmixin/i18n';
import {CaretDown} from '@phosphor-icons/react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link,
  Typography,
} from '@mui/material';
import {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {useScrollSpy} from '../hooks/useScrollSpy';
import {scrollToHeading, TocHeading} from '../utils/headings';

type TableOfContentsProps = {
  headings: TocHeading[];
  // 'rail' is the sticky desktop sidebar; 'inline' is the collapsible
  // disclosure shown above the article on smaller screens.
  variant?: 'rail' | 'inline';
};

type TocLinksProps = {
  headings: TocHeading[];
  activeId: string | null;
};

const TocLinks = ({headings, activeId}: TocLinksProps) => (
  <Box component="ul" sx={{listStyle: 'none', m: 0, p: 0}}>
    {headings.map((heading, index) => {
      const active = heading.id === activeId;
      return (
        <Box component="li" key={`${heading.id}-${index}`}>
          <Link
            href={`#${heading.id}`}
            underline="none"
            onClick={(event) => {
              event.preventDefault();
              scrollToHeading(heading.id);
            }}
            sx={{
              display: 'block',
              py: 0.5,
              pl: heading.level === 3 ? 3 : 1.5,
              borderLeft: 2,
              borderColor: active ? 'primary.main' : 'divider',
              color: active ? 'primary.main' : 'text.secondary',
              fontWeight: active ? 600 : 400,
              fontSize: '0.8125rem',
              lineHeight: 1.4,
              transition: 'color 150ms ease, border-color 150ms ease',
              '&:hover': {color: 'primary.main'},
            }}>
            {heading.text}
          </Link>
        </Box>
      );
    })}
  </Box>
);

const TableOfContents = ({headings, variant = 'rail'}: TableOfContentsProps) => {
  const {formatMessage} = useIntl();
  const ids = useMemo(() => headings.map((heading) => heading.id), [headings]);
  const activeId = useScrollSpy(ids);
  const onThisPageLabel = formatMessage(messages.common.onThisPage);

  if (headings.length === 0) {
    return null;
  }

  if (variant === 'inline') {
    return (
      <Accordion
        disableGutters
        elevation={0}
        sx={{
          mb: 3,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          backgroundColor: 'transparent',
          '&:before': {display: 'none'},
        }}>
        <AccordionSummary expandIcon={<CaretDown size={16} />}>
          <Typography variant="subtitle2" sx={{fontWeight: 600}}>
            {onThisPageLabel}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{pt: 0}}>
          <TocLinks headings={headings} activeId={activeId} />
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Box component="nav" aria-label={onThisPageLabel}>
      <Typography
        variant="overline"
        sx={{
          display: 'block',
          mb: 1,
          color: 'text.secondary',
          fontWeight: 600,
          letterSpacing: 1,
        }}>
        {onThisPageLabel}
      </Typography>
      <TocLinks headings={headings} activeId={activeId} />
    </Box>
  );
};

export default TableOfContents;
